import { agreementsPlatformApi } from '@/modules/agreements/services/platform-api';
import type {
  DocumentDraft,
  DocumentProgress,
} from '@/modules/agreements/platform/pages/document-create-page/types';

/**
 * The three writes the authoring flow makes, one function each.
 *
 * They live outside the hook because they are the flow's *contract with the
 * server*, not its React state: each one is a single route, each one is
 * idempotent against `progress`, and each one returns the id it produced or
 * throws. Keeping them here means the hook reads as a sequence of steps rather
 * than a wall of payload construction, and each payload can be read on its own.
 *
 * IDEMPOTENCE IS THE POINT. Every writer checks `progress` first and returns the
 * existing id rather than writing again. A retry after a partial failure
 * therefore re-runs only the steps that have not yet succeeded — it cannot
 * create a second document, because the first one's id is already recorded.
 */

/** Creates the document, unless it already exists. Returns the document id. */
export async function writeDocument(
  draft: DocumentDraft,
  progress: DocumentProgress,
): Promise<string> {
  if (progress.documentId) return progress.documentId;
  const created = await agreementsPlatformApi.createDocument({
    documentKey: draft.documentKey,
    title: draft.title.trim(),
    audience: draft.audience,
    requiredAction: draft.requiredAction,
  });
  return created.documentId;
}

/** Publishes the first version, unless one already exists. Returns the version id. */
export async function writeVersion(
  draft: DocumentDraft,
  progress: DocumentProgress,
  documentId: string,
): Promise<string> {
  if (progress.versionId) return progress.versionId;
  const version = await agreementsPlatformApi.publishVersion(documentId, {
    locale: draft.locale,
    isDefaultLocale: draft.isDefaultLocale,
    title: draft.versionTitle.trim(),
    content: draft.content,
    effectiveAt: new Date(`${draft.effectiveAt}T00:00:00.000Z`).toISOString(),
    attachments: [],
  });
  return version.versionId;
}

/** Creates the requirement, unless one already exists. Returns the requirement id. */
export async function writeRequirement(
  draft: DocumentDraft,
  progress: DocumentProgress,
  documentId: string,
  versionId: string,
): Promise<string> {
  if (progress.requirementId) return progress.requirementId;
  const requirement = await agreementsPlatformApi.createRequirement({
    documentId,
    versionId,
    // 'both' is a document-level statement; a requirement binds one audience, and
    // the vendor side is the one that carries the obligation.
    audience: draft.audience === 'both' ? 'tenant' : draft.audience,
    requiredAction: draft.requirementAction,
    applicability: {},
    reacceptanceRequired: draft.reacceptanceRequired,
    deadlineAt: draft.deadlineAt
      ? new Date(`${draft.deadlineAt}T00:00:00.000Z`).toISOString()
      : null,
    gracePeriodSeconds: null,
    // Only send the policy when the operator chose one. For a vendor signature
    // agreement the API requires it; for other shapes an absent value defaults to
    // session_reattested.
    ...(draft.verificationPolicy ? { verificationPolicy: draft.verificationPolicy } : {}),
  });
  return requirement.requirementId;
}
